import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_VAR,
} from "src/domains/shared/constants/styles/site";
import { SiteImage } from "../siteKit/media/SiteImage";
import { SiteVideo } from "../siteKit/media/SiteVideo";
import { revealRef } from "../siteKit/motion/reveal";

const STAGE_STYLES = {
  stage: {
    position: "relative",
    margin: 0,
    aspectRatio: "1224/680",
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: "linear-gradient(135deg,#F8FAFD 0%,#EAF0F8 100%)",
    isolation: "isolate",
    [SITE_MEDIA.tablet]: { aspectRatio: "4/5" },
    // 가로 터치 화면: 세로 4:5 틀은 화면 세 개 높이다 — 넓은 화면 비율로, 높이는 머리말 아래 화면 안에(폭을 줄여 가운데)
    [SITE_MEDIA.landscapeTouch]: {
      aspectRatio: "1224/680",
      maxWidth: `calc((100svh - ${SITE_HEADER_HEIGHT_PX}px - 32px) * 1224 / 680)`,
      marginInline: "auto",
    },
  },
  // 맥북 덮개를 크게 키워 왼쪽 위 모서리만 보이고 오른쪽·아래로 흘려 놓는다 — 안쪽 앱 화면 자리는 틀 없던 때(왼쪽 10%·위 9%)와 같다.
  // 테두리 길이는 모두 덮개 너비 기준(cqw)이라 화면 크기가 바뀌어도 비율이 같다
  laptop: {
    position: "absolute",
    left: "8.5%",
    top: "6.5%",
    width: "107%",
    containerType: "inline-size",
    [SITE_MEDIA.tablet]: { left: "6%", top: "8%", width: "190%" },
    [SITE_MEDIA.landscapeTouch]: { left: "8.5%", top: "6.5%", width: "107%" },
  },
  // 화면을 감싼 검은 유리 테두리와 은색 덮개 가장자리, 위 테두리 가운데 카메라
  lid: {
    position: "relative",
    padding: "1.7cqw 1.4cqw 0",
    borderRadius: "2.2cqw 2.2cqw 0 0",
    background: "#0C0C0E",
    boxShadow:
      "inset 0 0 0 1px rgba(255,255,255,.08),0 0 0 0.45cqw #CDD1D6,0 0 0 calc(0.45cqw + 1px) rgba(15,23,42,.14),0 36px 90px -24px rgba(28,44,66,.35)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "50%",
      top: "0.6cqw",
      width: "0.5cqw",
      height: "0.5cqw",
      transform: "translateX(-50%)",
      borderRadius: "50%",
      background: "radial-gradient(circle,#3A4049 30%,#15171A 70%)",
    },
  },
  // 앱 전체 화면 칸(16:10), 바탕은 3D 화면 하늘색
  screen: {
    position: "relative",
    aspectRatio: "16/10",
    overflow: "hidden",
    borderRadius: "0.7cqw 0.7cqw 0 0",
    background: "#C5D5E8",
  },
  // 3D 가 움직이는 영역(원본 3200×2000 의 x784 y32 2416×1904)
  video: {
    position: "absolute",
    left: "24.5%",
    top: "1.6%",
    width: "75.5%",
    height: "95.2%",
    objectFit: "fill",
    display: "block",
  },
  ui: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  },
} as const;

/**
 * 플랫폼 첫 화면(marketing-service-pages): 옅은 배경 위 크게 당겨 찍은 맥북 덮개 안에 앱 화면. 3D 모델이 도는 부분만 영상으로, 화면
 * UI(메뉴·패널·글자)는 영상보다 선명한 투명 그림으로 위에 정확히 겹친다 — 글자는 압축 없이 또렷하고 영상 비트는 3D 에 몰아준다.
 * 영상이 늘 다시 그려지므로 노트북 틀에는 filter(그림자 등)를 걸지 않는다(틀 전체가 매 프레임 다시 합성된다).
 */
export function PlatformHeroStage() {
  const { t } = useTranslation("landing");
  return (
    <Box
      component="figure"
      ref={revealRef}
      data-reveal="fade"
      sx={STAGE_STYLES.stage}
    >
      <Box sx={STAGE_STYLES.laptop}>
        <Box sx={STAGE_STYLES.lid}>
          <Box sx={STAGE_STYLES.screen}>
            <SiteVideo
              name="hero635_3d"
              loading="eager"
              ariaLabel={t("site.accurate.heroVideoLabel")}
              sx={STAGE_STYLES.video}
            />
            <SiteImage
              name="hero635_ui"
              sizes="(max-width: 900px) 180vw, 1273px"
              alt=""
              aria-hidden="true"
              priority
              placeholder="none"
              sx={STAGE_STYLES.ui}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
